import { Navigate } from 'react-router';
import { Center, Text, VStack } from '@chakra-ui/react';
import { ROUTES } from '../../constants/routes';
import { useWorkspaceId, useWorkspaceInitializing } from '../../contexts/WorkspaceContext';
import type { ReactNode } from 'react';
import { BowerNestLoader } from '../ui/BowerNestLoader';

/**
 * WorkspaceRoute wrapper component
 *
 * Ensures a workspace is selected before rendering child components.
 * This eliminates the need for every page to check workspace readiness.
 *
 * Usage:
 * <Route path="/collections" element={
 *   <WorkspaceRoute>
 *     <Projects />
 *   </WorkspaceRoute>
 * } />
 *
 * Benefits:
 * - Centralized workspace loading logic
 * - Pages can assume workspace is ready
 * - Consistent loading/error states
 * - No race conditions
 */

interface WorkspaceRouteProps {
  children: ReactNode;
}

export function WorkspaceRoute({ children }: WorkspaceRouteProps) {
  const workspaceId = useWorkspaceId();
  const isInitializing = useWorkspaceInitializing();

  // Show loading state while workspace is being initialized
  if (isInitializing) {
    return (
      <Center minH="60vh">
        <VStack spacing={4}>
          <BowerNestLoader size="lg" />
          <Text color="text.secondary">Loading workspace...</Text>
        </VStack>
      </Center>
    );
  }

  // Redirect to onboarding if no workspace is selected
  // User either needs to create one or select one
  if (!workspaceId) {
    return <Navigate to={ROUTES.onboarding} replace />;
  }

  // Workspace is selected - render children
  // Pages can now safely use workspaceId for API calls
  return <>{children}</>;
}
